import {Component, inject, OnInit, signal, TemplateRef, ChangeDetectionStrategy} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {SplitterType} from '../../forms/file-online-v2/directives/number-splitter.directive';
import {noop, Observable, Observer, of} from 'rxjs';
import {FullParcel, Parcel} from '../../_models/parcel';
import {TypeaheadMatch} from 'ngx-bootstrap/typeahead';
import {map, switchMap, tap} from 'rxjs/operators';
import {Addresses} from '../../_models/address';
import {ApiService} from '../../_services/api.service';
import {CustomFormControl} from '../../forms/file-online-v2/components/controls/CustomFormControl';
import {FormElement} from '../../forms/file-online-v2/models/WebFormTemplate';
import {WebFormEntry, WebFormSubmission} from '../../forms/file-online-v2/models/WebFormEntry';
import {AttachmentDto} from '../../forms/file-online-v2/models/AttachmentDto';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ViewportScroller} from '@angular/common';
import {EnvironmentService} from '../../_services/environment.service';
import {ActivatedRoute, Params} from '@angular/router';
import {noFutureDateValidator} from '../../forms/file-online-v2/validators/NoFutureDateValidator';
import {SiteConfigService} from '../../_services/site-config.service';

enum AdsSubmissionStatus {
  waiting,
  submitting,
  submitted
}

@Component({
    selector: 'app-ads820',
    templateUrl: './ads820.component.html',
    styleUrls: ['./ads820.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class Ads820Component implements OnInit {

  public readonly SplitterType = SplitterType;
  public readonly AdsSubmissionStatus = AdsSubmissionStatus;

  selectedItem: Parcel | null;
  parcels: Observable<Parcel[]>;

  searchField = new UntypedFormControl('');
  fileUploadControl: CustomFormControl;
  usingRpAutoFill = false;

  formGroup: UntypedFormGroup;
  siteConfig = inject(SiteConfigService);

  // For showing PDF preview
  modalRef: BsModalRef;
  pdfFillJson = signal(null);
  useAddressToolTip = 'Clicking "Use Address" will auto-fill the Assessment Number automatically';

  submissionStatus: AdsSubmissionStatus = AdsSubmissionStatus.waiting;
  error: string | null = null;
  enableSubmission = false;

  // For displaying custom alerts
  hasDamagePropSelected: boolean | null = null;
  hasSigned: boolean | null = null;

  damagePropMappings = {
    real_property: ['ain'],
    boat_aircraft_property: ['boat_aircraft_reg'],
    business_personal_property: ['assessment_number', 'company_name'],
    manufactured_property: ['manufactured_home_no'],
  };

  constructor(private fb: UntypedFormBuilder,
              private api: ApiService,
              private modalService: BsModalService,
              private viewportScroller: ViewportScroller,
              public environmentService: EnvironmentService,
              private route: ActivatedRoute) {
    this.formGroup = this.fb.group({
      damaged_property_street_address: this.fb.control('', [Validators.required]),
      damaged_property_city: this.fb.control('', [Validators.required]),
      damaged_property_zip: this.fb.control('', [Validators.required]),
      boat_aircraft_property: this.fb.control('', []),
      real_property: this.fb.control('', []),
      business_personal_property: this.fb.control('', []),
      manufactured_property: this.fb.control('', []),
      ain: this.fb.control('', []),
      // map: this.fb.control('', [Validators.minLength(4), Validators.maxLength(4)]),
      // page: this.fb.control('', [Validators.minLength(3), Validators.maxLength(3)]),
      // parcel: this.fb.control('', [Validators.minLength(3), Validators.maxLength(3)]),
      boat_aircraft_reg: this.fb.control('', []),
      assessment_number: this.fb.control('', []),
      company_name: this.fb.control('', []),
      manufactured_home_no: this.fb.control('', []),
      date_damage: this.fb.control('', [Validators.required, noFutureDateValidator()]),
      date_repaired: this.fb.control('', []),
      damage_cause: this.fb.control('', [Validators.maxLength(280)]),
      first_name: this.fb.control('', [Validators.required]),
      last_name: this.fb.control('', [Validators.required]),
      mail_street_address: this.fb.control('', [Validators.required]),
      mail_city: this.fb.control('', [Validators.required]),
      mail_state: this.fb.control('', [Validators.required]),
      mail_zip: this.fb.control('', [Validators.required]),
      signer1_phone: this.fb.control('', [Validators.required]),
      signer1_email: this.fb.control('', [Validators.required, Validators.email]),
      signature1: this.fb.control('', [Validators.required]),
    });

    this.restrictInput(['boat_aircraft_reg', 'assessment_number', 'manufactured_home_no']);

    Object.keys(this.damagePropMappings).forEach((key: string) => {
      const relatedControls = this.damagePropMappings[key];
      // Disable controls initially
      relatedControls.forEach((relatedControlId: string) => {
        this.formGroup.get(relatedControlId).disable();
      });

      // Listen for checkbox changes
      this.formGroup.get(key).valueChanges.subscribe((value) => {
        // Enable or disable the related controls
        relatedControls.forEach((relatedControlId: string) => {
          if (value) {
            this.formGroup.get(relatedControlId).enable();
          } else {
            this.formGroup.get(relatedControlId).disable();
            if (key !== 'real_property') {
              this.resetFields([relatedControlId]);
            }
          }
        });
      });
    });

    const template = new FormElement('na', 'na', 'na', 1, 1, '100%');
    template.uniqueFiles = new Set<string>();
    template.allowedExtensions = new Set<string>(['png', 'jpeg', 'pdf', 'docx', 'doc', 'jpg']);
    template.files = [];
    template.maxFiles = 10;
    template.maxTotalSize = 25 * 1024 * 1024;
    template.totalFileSize = 0;
    template.maxIndividualFileSize = 12 * 1024 * 1024;
    template.isPii = false;
    template.fileErrors = [];
    template.fileUploadOptional = true;
    this.fileUploadControl = new CustomFormControl('', template);
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (params.dev) {
        this.enableSubmission = true;
      }
    });

    this.parcels = new Observable((observer: Observer<string>) => {
      observer.next(this.searchField.value);
    }).pipe(
      switchMap((query: string) => {
        if (query) {
          return this.api.getAddress(query).pipe(
            map((data: Addresses) => data && data.Parcels || []),
            tap(() => noop, _ => {
            })
          );
        }

        return of([]);
      }));
  }

  restrictInput(controlIds: string[]): void {
    controlIds.forEach((controlId: string) => {
      this.formGroup.get(controlId).valueChanges.subscribe((value: string | undefined) => {
        if (!value) {
          return;
        }

        const stripped = value.replace(/[^a-zA-Z0-9]/g, '');
        if (value !== stripped) {
          this.formGroup.get(controlId).setValue(stripped, { emitEvent: false });
        }
      });
    });
  }

  checkHasDamagePropertySelected(): boolean {
    let hasOne = false;
    Object.keys(this.damagePropMappings).forEach((key: string) => {
      if (this.isChecked(key)) {
        hasOne = true;
      }
    });

    return hasOne;
  }

  onClickSubmit(template: TemplateRef<any>): void {
    this.hasDamagePropSelected = this.checkHasDamagePropertySelected();
    this.hasSigned = (this.formGroup.get('signature1')?.value ?? '').length > 0;
    if (!this.formGroup.valid || !this.hasRequiredFiles() || !this.hasDamagePropSelected || !this.hasSigned) {
      this.modalRef = this.modalService.show(template);
      // Highlight all errors
      Object.values(this.formGroup.controls).forEach((control) => {
        control.markAsTouched({ onlySelf: true });
      });
      return;
    }

    const serialized = this.simpleSerialization();

    const files: AttachmentDto[] = [];
    for (const file of this.fileUploadControl.element.files) {
      files.push({ isPii: false, file });
    }

    this.submissionStatus = AdsSubmissionStatus.submitting;
    this.error = null;

    this.api.submitAdsForm(serialized, files).subscribe((_) => {
      this.submissionStatus = AdsSubmissionStatus.submitted;
      this.formGroup.reset();
      this.fileUploadControl.element.totalFileSize = 0;
      this.fileUploadControl.element.files = [];
      this.fileUploadControl.element.uniqueFiles.clear();
    }, error => {
      console.error(error);
      this.error = error.toString();
      this.submissionStatus = AdsSubmissionStatus.waiting;
    });
  }

  // isChecked(controlId: string): boolean {
  //   return this.formGroup.get(controlId).value.length > 0;
  // }

  hasRequiredFiles(): boolean {
    if (this.fileUploadControl.element.fileUploadOptional) {
      return true;
    }

    if (this.fileUploadControl.element.files.length === 0) {
      if (this.fileUploadControl.element.fileErrors.length === 0) {
        this.fileUploadControl.element.fileErrors = ['You must upload at least one file.'];
      }

      return false;
    }
  }

  isChecked(controlId: string): boolean {
    return this.formGroup.get(controlId).value === true;
  }

  simpleSerialization(): string {
    let entries = [];

    const dict = {};
    Object.keys(this.damagePropMappings).forEach((key: string) => {
      const relatedControls = this.damagePropMappings[key];
      relatedControls.forEach((relatedControlId: string) => {
        dict[relatedControlId] = key;
      });
    });

    // Serialize main keys
    Object.keys(this.formGroup.controls).forEach((key: string) => {
      const value = this.formGroup.get(key).value ?? '';

      if (dict[key]) {
        if (this.isChecked(dict[key])) {
          if (key === 'ain') {
            entries.push(new WebFormEntry('text', key, value.replace(/-/g, ''), false, false, ''));
          } else {
            entries.push(new WebFormEntry('text', key, value, false, false, ''));
          }
        }
      } else if (key === 'boat_aircraft_property' || key === 'real_property' || key === 'business_personal_property' || key === 'manufactured_property') {
        entries.push(new WebFormEntry('checkbox', key, value.toString(), false, false, ''));
      } else if (key === 'date_damage' || key === 'date_repaired') {
        const [year, month, day] = value.split(/-/g);
        if (year === undefined || month === undefined || day === undefined) {
          entries.push(new WebFormEntry('text', key, '', false, false, ''));
        } else {
          const formattedDate = `${month}/${day}/${year}`;
          entries.push(new WebFormEntry('text', key, formattedDate, false, false, ''));
        }
      } else {
        entries.push(new WebFormEntry('text', key, value, false, false, ''));
      }
    });

    // Add additional keys
    entries.push(this.serializeMultiple('final_address_damaged_property', ['damaged_property_street_address', 'damaged_property_city', 'damaged_property_zip'], ' , '));
    entries.push(this.serializeMultiple('owner_name', ['first_name', 'last_name'], ' '));
    entries.push(this.serializeMultiple('Mail Street Address', ['mail_street_address'], ' , '));
    entries.push(this.serializeMultiple('city_state_zip', ['mail_city', 'mail_state', 'mail_zip'], ' , '));

    const formatted = Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short'
    }).format(new Date()).replace(',', '');

    entries.push(new WebFormEntry('text', 'Signature1 Date', formatted, false, false, ''));

    const ignoredKeys = new Set<string>([
      'mail_street_address',
      'mail_city',
      'mail_state',
      'mail_zip',
      'first_name',
      'last_name',
    ]);

    entries = entries.filter(value => !ignoredKeys.has(value));

    const usingAutoFill = this.usingRpAutoFill;
    const meta = {
      usingRpAutoFill: usingAutoFill,
      region: this.selectedItem?.Region
    };

    const webFormSubmission = new WebFormSubmission();
    webFormSubmission.formId = 'ADS-820.2';
    webFormSubmission.watermark = true;
    webFormSubmission.pdfUrl = 'https://res.cloudinary.com/los-angeles-county-assessor/image/upload/v1790371695/eFile/Forms/PROD/ADS-820.2.pdf';
    webFormSubmission.meta = JSON.stringify(meta);
    webFormSubmission.signers = [];
    webFormSubmission.entries = entries;
    return JSON.stringify(webFormSubmission);
  }

  serializeMultiple(targetKey: string, controls: string[], joinChar: string): WebFormEntry {
    const values = [];
    controls.forEach((control: string) => {
      values.push(this.formGroup.get(control)?.value);
    });

    return new WebFormEntry('text', targetKey, values.join(joinChar), true, false, '');
  }

  handleTypeheadOnSelect(event: TypeaheadMatch): void {
    this.selectedItem = event.item;
    const fullAddress = event.item.SitusStreet + ', ' +  event.item.SitusCity + ', ' +  event.item.SitusZipCode;
    this.searchField.setValue(fullAddress, { emitEvent: false });

    this.api.getParcel(event.item.AIN).subscribe((item: FullParcel) => {
      if (this.selectedItem?.AIN === item.Parcel.AIN) {
        this.selectedItem = item.Parcel;
      }
    }, error => {
      console.error(error);
    });
  }

  onClickUseAddress(): void {
    if (!this.selectedItem) {
      return;
    }

    this.formGroup.get('damaged_property_street_address').setValue(this.selectedItem.SitusStreet);
    this.formGroup.get('damaged_property_city').setValue(this.selectedItem.SitusCity);
    this.formGroup.get('damaged_property_zip').setValue(this.selectedItem.SitusZipCode);

    this.toggleDamagedProp(false);
    this.toggleControlState(['ain'], false);

    // Fill in AIN
    const value = this.selectedItem.AIN.replace(/-/g, '');
    this.formGroup.get('ain').setValue(value);
    this.formGroup.get('real_property').setValue(true, { emitEvent: false });

    this.searchField.setValue('');
    this.usingRpAutoFill = true;
  }

  onClickClearAddress(): void {
    this.toggleDamagedProp(true);
    this.toggleControlState(['ain'], true);
    this.resetFields(['damaged_property_street_address', 'damaged_property_city', 'damaged_property_zip']);
    this.resetFields(['ain']);
    this.selectedItem = null;
    this.usingRpAutoFill = false;
    this.formGroup.get('real_property').setValue(false, { emitEvent: true });
  }

  onClickPreview(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
    const json = this.simpleSerialization();
    this.pdfFillJson.set(json);
  }

  toggleDamagedProp(enable: boolean): void {
    const fields = ['damaged_property_street_address', 'damaged_property_city', 'damaged_property_zip'];
    this.toggleControlState(fields, enable);
  }

  toggleControlState(ids: string[], enable: boolean): void {
    ids.forEach(id => {
      if (enable) {
        this.formGroup.get(id).enable();
      } else {
        this.formGroup.get(id).disable();
      }
    });
  }

  resetFields(ids: string[]): void {
    ids.forEach(id => {
      this.formGroup.get(id).reset();
    });
  }

  onStartNewSubmission(): void {
    this.selectedItem = null;
    this.usingRpAutoFill = false; // Reset
    this.submissionStatus = AdsSubmissionStatus.waiting;
    this.toggleDamagedProp(true);
    this.viewportScroller.scrollToAnchor('formTop');
  }
}
